import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getProjectTeamErrorMessage } from '../api/api';
import { useProjectInvitation } from '../hooks/useProjectTeam';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import Navigation from '@/shared/components/home/Navigation';
import Section from '@/shared/components/home/Section';
import { STYLES } from '@/shared/components/home/constants';

export const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const {
    invitedUser,
    isLoading,
    error: invitationError,
    accept,
  } = useProjectInvitation(token);
  const error = !token
    ? 'Invalid invitation link'
    : invitationError
      ? getProjectTeamErrorMessage(invitationError, 'Failed to load invitation')
      : accept.error
        ? getProjectTeamErrorMessage(accept.error, 'Failed to accept invitation')
        : null;

  useEffect(() => {
    if (!accept.isSuccess) return;
    const timeout = window.setTimeout(() => navigate('/signup'), 2000);
    return () => window.clearTimeout(timeout);
  }, [accept.isSuccess, navigate]);

  const handleAcceptInvitation = async () => {
    if (!token) return;
    try {
      await accept.mutateAsync();
    } catch {
      // The mutation error is rendered from the shared API error shape.
    }
  };

  const handleNavigate = (sectionId: string) => {
    // Navigate to home page with section
    navigate(`/#${sectionId}`);
  };

  if (isLoading && token) {
    return (
      <div className="min-h-screen bg-maintenance-navy flex flex-col">
        <div className="p-2 md:p-5">
          <Navigation onNavigate={handleNavigate} />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-white mx-auto mb-4" />
            <p className="text-white text-lg">Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (accept.isSuccess) {
    return (
      <div className="min-h-screen bg-maintenance-navy flex flex-col">
        <div className="p-2 md:p-5">
          <Navigation onNavigate={handleNavigate} />
        </div>
        <Section
          id="invitation-success"
          backgroundImage="/static/images/sf2_1600.webp"
          showBackToTop={false}
          overlayClassName={STYLES.overlay.heroGradient}
        >
          <div className="max-w-2xl mx-auto text-center py-12">
            <div className={`${STYLES.card} rounded-2xl p-8 md:p-12`}>
              <div className="mx-auto mb-6 w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-green-400" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Invitation Accepted!
              </h1>
              <p className="text-gray-300 text-lg mb-6">
                Welcome to {invitedUser?.project?.name}. Redirecting you to sign up...
              </p>
              <Loader2 className="h-6 w-6 animate-spin text-white mx-auto" />
            </div>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-maintenance-navy flex flex-col">
      <div className="p-2 md:p-5">
        <Navigation onNavigate={handleNavigate} />
      </div>
      
      <Section
        id="accept-invitation"
        backgroundImage="/static/images/sf2_1600.webp"
        showBackToTop={false}
        overlayClassName={STYLES.overlay.heroGradient}
      >
        <div className="max-w-3xl mx-auto py-12">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {invitedUser && (
            <div className={`${STYLES.card} rounded-2xl p-8 md:p-12`}>
              <div className="text-center mb-8">
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
                  You've Been Invited!
                </h1>
                <p className="text-lg md:text-xl text-gray-300">
                  Join the{' '}
                  <span className="font-semibold text-white">
                    {invitedUser.project?.name}
                  </span>{' '}
                  project
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-6 mb-8 border border-white/20">
                <div className="space-y-4 text-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 font-medium">Project:</span>
                    <span className="font-semibold text-white text-right">{invitedUser.project?.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 font-medium">Client:</span>
                    <span className="font-semibold text-white text-right">{invitedUser.client?.name}</span>
                  </div>
                  {invitedUser.role && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 font-medium">Role:</span>
                      <span className="font-semibold text-white text-right">{invitedUser.role}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 font-medium">Email:</span>
                    <span className="font-semibold text-white text-right break-all">{invitedUser.email}</span>
                  </div>
                </div>

                {invitedUser.permissions && invitedUser.permissions.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-white/20">
                    <p className="text-gray-400 font-medium mb-4">Access Permissions:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {invitedUser.permissions.map((permission) => (
                        <div key={permission.id} className="flex items-center text-white">
                          <CheckCircle2 className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                          <span>{permission.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Button
                onClick={handleAcceptInvitation}
                disabled={accept.isPending}
                className={`w-full px-6 py-4 text-lg font-semibold ${STYLES.button.primary}`}
              >
                {accept.isPending ? (
                  <>
                    <Loader2 className="inline-block mr-2 h-5 w-5 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  'Accept Invitation'
                )}
              </Button>

              <p className="text-center text-gray-400 text-sm mt-6">
                By accepting, you'll be able to create an account and access the project resources.
              </p>
            </div>
          )}
        </div>
      </Section>

      {/* Footer */}
      <div className="bg-maintenance-navy/95 py-4 text-center">
        <p className="text-gray-400 text-sm">
          &copy; 2026 Ironview 360. All rights reserved.
        </p>
      </div>
    </div>
  );
};

